import { IFormAction, IFormActions } from '../../@types/entityTypes';
import {
  IAssemblyComponentReservationMutationResult,
  IAssemblyComponentReservationMutationVariables,
  IAssemblyReservation,
  IAssemblyReservationDetail,
} from '../../graphql/schema/p4types';
import AssemblyComponentReservationMutation from '../../graphql/mutations/product/AssemblyComponentReservationMutation.graphql';
import { useComponentQueryErrorHandling } from '../../utils/parseErrorResponse';
import getClientMutationId from '../../utils/getClientMutationId';
import { useBackgroundMutation } from '../../utils/BackgroundJobs';
import { InfoButton, PrimaryButton } from '../../components/connectedForm/Button';
import { useAssemblyTreeContext } from '../../views/product/AssemblyTreeContext';

export type ExtendedAssemblyReservationDetail = IAssemblyReservationDetail & {
  quantity?: number;
};

export type ExtendedAssemblyReservation = IAssemblyReservation & {
  assemblyReservationDetails: ExtendedAssemblyReservationDetail;
};

export enum AssemblyReservationFormAction {
  Reserve = 'Reserve',
  ReserveAll = 'RESERVE_ALL',
}

export const assemblyReservationFormActionsForPermissions: IFormActions = [
  {
    actionReference: AssemblyReservationFormAction.Reserve,
  },
  {
    actionReference: AssemblyReservationFormAction.ReserveAll,
  },
];

export const useAssemblyReservationFormActions: (
  assemblyReservations: Array<ExtendedAssemblyReservation>,
  reserveAllHandler: () => void,
  onCompleteJob: () => void
) => Array<IFormAction<AssemblyReservationFormAction>> = (assemblyReservations, reserveAllHandler, onCompleteJob) => {
  const { assembly: rootAssembly, reserveAll } = useAssemblyTreeContext();

  const handleQueryError = useComponentQueryErrorHandling();
  const [reserve] = useBackgroundMutation<
    IAssemblyComponentReservationMutationResult,
    IAssemblyComponentReservationMutationVariables
  >(AssemblyComponentReservationMutation, {
    jobTitle: 'Reserving components',
    onError: handleQueryError,
    onCompleteJob,
  });

  return [
    {
      key: 'reserve',
      text: 'Reserve',
      individualButton: true,
      individualButtonComponent: PrimaryButton,
      actionReference: AssemblyReservationFormAction.Reserve,
      onClick: () => {
        reserve({
          variables: {
            input: {
              clientMutationId: getClientMutationId(),
              assemblyId: rootAssembly?.id,
              reserveAll: reserveAll,
              assemblyReservationRequests: assemblyReservations?.flatMap(
                (assemblyReservation: ExtendedAssemblyReservation) => {
                  return assemblyReservation.assemblyReservationDetails
                    ?.filter((assemblyReservationDetail) => {
                      return (assemblyReservationDetail as ExtendedAssemblyReservation).quantity;
                    })
                    .map((assemblyReservationDetail) => {
                      return {
                        assemblyId: assemblyReservation?.assembly.id,
                        stockLotId: assemblyReservationDetail?.stockLot?.id,
                        productComponentId: assemblyReservation?.productComponent?.id,
                        quantity: (assemblyReservationDetail as ExtendedAssemblyReservationDetail).quantity,
                      };
                    });
                }
              ),
            },
          },
        });
      },
      disabled:
        !reserveAll &&
        !assemblyReservations
          ?.flatMap((assemblyReservation) => assemblyReservation?.assemblyReservationDetails)
          .some(
            (assemblyReservationDetail) =>
              (assemblyReservationDetail as ExtendedAssemblyReservationDetail)?.quantity !== undefined &&
              parseFloat((assemblyReservationDetail as ExtendedAssemblyReservationDetail).quantity as any) !== 0.0
          ),
    },
    {
      key: 'reserveAll',
      text: 'Reserve All',
      individualButton: true,
      individualButtonComponent: InfoButton,
      actionReference: AssemblyReservationFormAction.ReserveAll,
      onClick: () => {
        // this will change the reservation level of any existing requests
        reserveAllHandler();
      },
    },
  ];
};
